/**
 * Security Context Menu Service
 *
 * ENGINE AGNOSTIC: Context menu actions work for both MSSQL and PostgreSQL.
 * Engine-specific behavior is handled by the script generators in the backend.
 */

import { ContextMenuAction } from '../../explorer/models/TreeModel';
import { ContextMenuActionType } from '../../explorer/services/contextMenuService';

/**
 * Security object types that have context menus
 */
export type SecurityObjectType =
  | 'login'
  | 'server-role'
  | 'database-user'
  | 'database-role'
  | 'schema'
  | 'login-role'
  | 'group-role'
  | 'oracle-user'
  | 'oracle-role';

/**
 * Check if a node type is a security object
 */
export function isSecurityObjectType(nodeType: string): nodeType is SecurityObjectType {
  return [
    'login',
    'server-role',
    'database-user',
    'database-role',
    'schema',
    'login-role',
    'group-role',
    'oracle-user',
    'oracle-role',
  ].includes(nodeType);
}

/**
 * Check if a node type is a security folder
 */
export function isSecurityFolderType(nodeType: string): boolean {
  return [
    'server-security-folder',
    'logins-folder',
    'server-roles-folder',
    'pg-roles-folder',
    'login-roles-folder',
    'group-roles-folder',
    'database-security-folder',
    'users-folder',
    'database-roles-folder',
    'schemas-folder',
    'oracle-roles-folder',
  ].includes(nodeType);
}

/**
 * Generate context menu actions for a security object
 */
/**
 * Map security object types to human-readable labels for the copy action
 */
const securityTypeLabelMap: Record<SecurityObjectType, string> = {
  'login': 'login',
  'server-role': 'role',
  'database-user': 'user',
  'database-role': 'role',
  'schema': 'schema',
  'login-role': 'role',
  'group-role': 'role',
  'oracle-user': 'user',
  'oracle-role': 'role',
};

/**
 * Generate context menu actions for a security object
 */
export function generateSecurityObjectContextMenuActions(
  _nodeType: SecurityObjectType
): ContextMenuAction[] {
  const actions: ContextMenuAction[] = [];

  // Copy name action
  const typeLabel = securityTypeLabelMap[_nodeType] || _nodeType.replace(/-/g, ' ');
  actions.push({ id: 'copy-name', label: `Copy ${typeLabel} name`, icon: 'clipboard' });

  // Script As submenu
  const scriptAsActions: ContextMenuAction[] = [
    {
      id: ContextMenuActionType.SCRIPT_AS_CREATE,
      label: 'CREATE To...',
      icon: 'file-plus',
    },
    {
      id: ContextMenuActionType.SCRIPT_AS_ALTER,
      label: 'ALTER To...',
      icon: 'file-edit',
    },
  ];

  scriptAsActions.push({
    id: ContextMenuActionType.SCRIPT_AS_DROP,
    label: 'DROP To...',
    icon: 'file-minus',
  });

  // Add Script As submenu
  actions.push({
    id: 'script-as',
    label: 'Script As',
    icon: 'code',
    submenu: scriptAsActions,
  });

  // Add separator
  actions.push({ id: 'separator-1', label: '', isSeparator: true });

  // Properties action
  actions.push({
    id: ContextMenuActionType.PROPERTIES,
    label: 'Properties...',
    icon: 'settings',
  });

  // Add separator
  actions.push({ id: 'separator-2', label: '', isSeparator: true });

  // Refresh action
  actions.push({
    id: ContextMenuActionType.REFRESH,
    label: 'Refresh',
    icon: 'refresh',
  });

  return actions;
}

/**
 * Execute a security context menu action
 */
export function executeSecurityContextMenuAction(
  actionId: string,
  nodeId: string,
  handlers: {
    onRefresh?: (nodeId: string) => void;
    onScriptCreate?: (nodeId: string) => void;
    onScriptAlter?: (nodeId: string) => void;
    onScriptDrop?: (nodeId: string) => void;
    onProperties?: (nodeId: string) => void;
  }
): void {
  switch (actionId) {
    case ContextMenuActionType.REFRESH:
      handlers.onRefresh?.(nodeId);
      break;
    case ContextMenuActionType.SCRIPT_AS_CREATE:
      handlers.onScriptCreate?.(nodeId);
      break;
    case ContextMenuActionType.SCRIPT_AS_ALTER:
      handlers.onScriptAlter?.(nodeId);
      break;
    case ContextMenuActionType.SCRIPT_AS_DROP:
      handlers.onScriptDrop?.(nodeId);
      break;
    case ContextMenuActionType.PROPERTIES:
      handlers.onProperties?.(nodeId);
      break;
    default:
      console.warn(`Unhandled security action: ${actionId}`);
  }
}
